// Shared helpers for the hand-built SVG charts. No dependencies.
//
// Colors come from CSS custom properties (--chart-1…6, --chart-grid, …) so
// every chart re-themes with the tenant brand and adapts to dark mode with
// zero JS. Reference them as strings: `var(--chart-2)`.

/** The categorical series palette, by slot. Slot 1 tracks the tenant brand. */
export const SERIES = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
] as const;

/** Pick a series color by index (wraps, but callers should keep ≤6). */
export function seriesColor(i: number): string {
  return SERIES[i % SERIES.length];
}

/** Compact number formatting for axis ticks / value labels: 1,284 / 12.9K / $4.2M. */
export function compact(n: number, opts: { currency?: boolean } = {}): string {
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  const cur = opts.currency ? '$' : '';
  if (a >= 1_000_000) return `${sign}${cur}${trim(a / 1_000_000)}M`;
  if (a >= 1_000) return `${sign}${cur}${trim(a / 1_000)}K`;
  return `${sign}${cur}${a.toLocaleString()}`;
}
function trim(n: number): string {
  return (Math.round(n * 10) / 10).toString();
}

/** A "nice" upper bound + tick step for a value range starting at 0. */
export function niceScale(max: number, ticks = 4): { max: number; step: number; ticks: number[] } {
  if (max <= 0) return { max: 1, step: 1, ticks: [0, 1] };
  const rawStep = max / ticks;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const niceNorm = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  const step = niceNorm * mag;
  const niceMax = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = 0; v <= niceMax + step / 2; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return { max: niceMax, step, ticks: out };
}

export interface Pt { x: number; y: number }

/** Map data indices → x positions and values → y positions inside a box. */
export function project(
  values: number[],
  box: { x: number; y: number; w: number; h: number },
  domainMax: number,
  domainMin = 0,
): Pt[] {
  const n = values.length;
  const span = domainMax - domainMin || 1;
  return values.map((v, i) => ({
    x: box.x + (n <= 1 ? box.w / 2 : (i / (n - 1)) * box.w),
    y: box.y + box.h - ((v - domainMin) / span) * box.h,
  }));
}

/** A smooth-ish polyline path (straight segments — crisp, legible, honest). */
export function linePath(pts: Pt[]): string {
  if (pts.length === 0) return '';
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${round(p.x)} ${round(p.y)}`).join(' ');
}

/** An area path closed to the baseline. */
export function areaPath(pts: Pt[], baselineY: number): string {
  if (pts.length === 0) return '';
  const first = pts[0];
  const last = pts[pts.length - 1];
  return (
    `M${round(first.x)} ${round(baselineY)} ` +
    pts.map((p) => `L${round(p.x)} ${round(p.y)}`).join(' ') +
    ` L${round(last.x)} ${round(baselineY)} Z`
  );
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
